import * as React from 'react';

export interface CloseOnBlurProps {
  onBlur: (clicked: boolean) => void;
  children?: React.ReactNode;
  dropdownRef?: React.RefObject<HTMLDivElement>;
}
export interface CloseOnBlurState {
  windowHasFocus: boolean;
  componentDidBlur: boolean;
}

export class CloseOnBlur extends React.Component<CloseOnBlurProps, CloseOnBlurState> {
  private timeoutHandle: any;
  private wrapperRef!: HTMLDivElement;
  private pointerStartedOutside = false;
  public state:Readonly<CloseOnBlurState> = {
    windowHasFocus: true,
    componentDidBlur: false
  };

  public componentDidMount() {
    document.addEventListener('mousedown', this.handleMouseDown);
  }

  public componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleMouseDown);
    window.removeEventListener('blur', this.windowBlur);
    clearTimeout(this.timeoutHandle);
  }

  private windowBlur = () => {
    this.setState({
      windowHasFocus: false
    });
  };

  private isChild(elementInFocus: any, parent: HTMLDivElement) {
    let node = elementInFocus;
    while (node) {
      if (node === parent) {
        return true;
      }
      node = node.parentNode;
    }
    return false;
  }

  private isInside = (element: Node) => this.isChild(element, this.wrapperRef)
    || !!this.props.dropdownRef?.current?.contains(element);

  private isDropdownTarget = (element: Node) => !!this.props.dropdownRef?.current?.contains(element);

  private getFocusTarget = (event: any): Node | null =>
    event.nativeEvent.relatedTarget || event.nativeEvent.originalTarget;

  private didComponentBlur = (event: any) => {
    const focusTarget = this.getFocusTarget(event);
    if (!focusTarget || focusTarget === document.body) {
      return this.pointerStartedOutside;
    }

    return !this.isInside(focusTarget);
  };

  private handleMouseDown = (event: MouseEvent) => {
    this.pointerStartedOutside = !this.isInside(event.target as Node);
  };

  private setWrapperRef = (element: HTMLDivElement) => {
    this.wrapperRef = element;
  };

  /**
   * Swaps the execution order of window.blur and component.blur by delaying events by one tick.
   * DOM nodes can now be inspected while debugging.
   */
  private handleBlur = (event: any) => {
    if (this.isDropdownTarget(event.target)) {
      return;
    }

    this.setState({
      componentDidBlur: this.didComponentBlur(event)
    });
    clearTimeout(this.timeoutHandle);
    this.timeoutHandle = setTimeout(this.delayedBlurEvent, 1);
  };

  private delayedBlurEvent = () => {

    const shouldTriggerOnBlur = this.state.componentDidBlur && this.state.windowHasFocus;

    this.pointerStartedOutside = false;
    window.removeEventListener('blur', this.windowBlur);
    this.setState({ componentDidBlur: false });
    if (shouldTriggerOnBlur) {
      this.props.onBlur(false);
    }
  };

  private handleFocus = () => {
    clearTimeout(this.timeoutHandle);
    window.removeEventListener('blur', this.windowBlur);
    window.addEventListener('blur', this.windowBlur);
    this.setState({
      windowHasFocus: true,
      componentDidBlur: false
    });
  };

  public render() {
    return (<div
      ref={this.setWrapperRef}
      onFocus={this.handleFocus}
      onBlur={this.handleBlur}
    >
      {this.props.children}
    </div>);
  }
}
